// Живой канал кабинета: одно соединение на сеанс, знак «сходи за новым».
//
// ## Почему соединение живёт в модуле, а не в экране
//
// Канал не привязан к экрану. Подписка, заведённая в компоненте списка, умирает при переходе
// в другой раздел, и человек перестаёт узнавать о новом ровно тогда, когда занят другим делом.
// Поэтому соединение поднимается один раз при входе и живёт до выхода.
//
// ## Живость проверяем сами
//
// Обрыв транспорта не всегда сопровождается закрытием: уход в туннель, смена сети, таймаут
// прокси. Сервер шлёт пульс каждые 30 секунд; если тишина длится дольше срока ниже, соединение
// считается мёртвым и пересоздаётся. Донорский инцидент 6 августа 2026: библиотека считала
// канал живым по своему состоянию, а на сервере подписки не было вовсе — сокет выглядел
// здоровым, событий не приходило, и никто об этом не знал.

import { apiBaseUrl } from '@/shared/api/client';

/** Кадр канала. Данных в нём нет: только вид и наибольший номер.
 *
 * Вид `chats` кабинету не адресован: переписка живёт в телефоне (`ПРАВИЛА.md`, раздел 1.2), и
 * реагировать на него здесь не на что. Он перечислен намеренно, чтобы было видно, что кадр
 * известен и пропускается осознанно, а не забыт. Неизвестные виды кабинет тоже пропускает
 * молча — на этом держится аддитивность канала. */
export interface RealtimeFrame {
  type: 'hello' | 'notifications' | 'ping' | 'chats';
  high_water: number;
}

/** Тишина дольше этого срока означает мёртвое соединение: пульс сервера идёт раз в 30 секунд. */
const МОЛЧАНИЕ_МС = 75_000;

/** Пауза перед переподключением растёт до потолка: сеть в поле бывает недоступна долго. */
const ПАУЗА_МИН_МС = 1_000;
const ПАУЗА_МАКС_МС = 30_000;

export interface ChannelHandlers {
  /** Пришёл знак «появилось новое». Данные берутся лентой, а не из кадра. */
  onNews: (highWater: number) => void;
  /** Соединение открыто и приветствие получено. */
  onHello?: (highWater: number) => void;
}

export interface Channel {
  close: () => void;
}

function адресКанала(): string {
  // Пустая база — «тот же адрес, что и страница»: конструктор вебсокета относительный путь
  // принимает не во всех движках, поэтому адрес страницы подставляется явно.
  const база = apiBaseUrl() || window.location.origin;
  // Схема канала выводится из схемы обмена: за обратным прокси это wss, локально ws.
  return `${база.replace(/^http/, 'ws')}/api/v1/realtime`;
}

/**
 * Открыть канал. Токен передаётся ПЕРВЫМ КАДРОМ, а не в адресе: адрес целиком попадает в
 * журналы обратного прокси, и токен доступа осел бы там текстом.
 */
export function openRealtimeChannel(
  getToken: () => string | null,
  handlers: ChannelHandlers,
): Channel {
  let socket: WebSocket | null = null;
  let сторож: ReturnType<typeof setTimeout> | null = null;
  let переподключение: ReturnType<typeof setTimeout> | null = null;
  let пауза = ПАУЗА_МИН_МС;
  let закрыт = false;

  const снятьСторожа = () => {
    if (сторож !== null) {
      clearTimeout(сторож);
      сторож = null;
    }
  };

  const завестиСторожа = () => {
    снятьСторожа();
    сторож = setTimeout(() => {
      // Молчание дольше пульса: соединение зомби. Закрываем сами — иначе оно останется
      // «открытым» навсегда и человек не узнает ни о чём.
      socket?.close();
    }, МОЛЧАНИЕ_МС);
  };

  const запланироватьПовтор = () => {
    if (закрыт || переподключение !== null) {
      return;
    }
    переподключение = setTimeout(() => {
      переподключение = null;
      подключиться();
    }, пауза);
    пауза = Math.min(пауза * 2, ПАУЗА_МАКС_МС);
  };

  function подключиться(): void {
    if (закрыт) {
      return;
    }
    const токен = getToken();
    if (токен === null) {
      // Ещё не вошли или уже вышли: канал без токена бессмыслен, ждём следующей попытки.
      запланироватьПовтор();
      return;
    }

    let ws: WebSocket;
    try {
      ws = new WebSocket(адресКанала());
    } catch {
      запланироватьПовтор();
      return;
    }
    socket = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'auth', token: токен }));
      завестиСторожа();
    };

    ws.onmessage = (событие) => {
      завестиСторожа();
      let кадр: RealtimeFrame;
      try {
        кадр = JSON.parse(String(событие.data)) as RealtimeFrame;
      } catch {
        return;
      }
      if (кадр.type === 'hello') {
        // Соединение состоялось: паузу сбрасываем, иначе после долгого обрыва она осталась бы
        // на потолке и следующий обрыв стоил бы человеку полминуты слепоты.
        пауза = ПАУЗА_МИН_МС;
        handlers.onHello?.(кадр.high_water);
        return;
      }
      if (кадр.type === 'notifications') {
        handlers.onNews(кадр.high_water);
      }
    };

    ws.onclose = () => {
      снятьСторожа();
      socket = null;
      запланироватьПовтор();
    };

    ws.onerror = () => {
      // Ошибку не показываем человеку: канал — ускоритель, а не единственный путь. Пропущенный
      // кадр означает задержку до следующего запроса ленты, а не потерю уведомления.
      ws.close();
    };
  }

  // Возврат из фона: подключаемся немедленно, не дожидаясь очередной паузы.
  //
  // На рабочем столе окно теряет фокус, не теряя канала, и трогать его не за чем. На планшете
  // всё иначе: пока приложение не на экране, система замораживает страницу и рвёт соединение.
  // Само оно восстановится — но по нарастающей паузе, а она к этому моменту доросла до
  // полминуты. Полминуты слепоты после КАЖДОГО возврата, а между приложениями переключаются
  // десятки раз за день.
  const вернулисьНаЭкран = () => {
    if (закрыт || document.visibilityState !== 'visible') {
      return;
    }
    if (socket !== null && socket.readyState === WebSocket.OPEN) {
      return;
    }
    if (переподключение !== null) {
      clearTimeout(переподключение);
      переподключение = null;
    }
    // Пауза копилась, пока экрана не было; для нового захода она бессмысленна.
    пауза = ПАУЗА_МИН_МС;
    socket?.close();
    socket = null;
    подключиться();
  };
  document.addEventListener('visibilitychange', вернулисьНаЭкран);

  подключиться();

  return {
    close() {
      закрыт = true;
      document.removeEventListener('visibilitychange', вернулисьНаЭкран);
      снятьСторожа();
      if (переподключение !== null) {
        clearTimeout(переподключение);
        переподключение = null;
      }
      socket?.close();
      socket = null;
    },
  };
}
